import { useSyncExternalStore } from "react";
import type { AdviceLocale } from "@/features/forms/shared/advisory-locale";
import type {
  PanelFamilyKey,
  RecordFamilyKey,
  RecordSubjectKind,
} from "@/features/forms/shared/record/record-family";

export interface RecordPrepareState {
  id: string;
  family: PanelFamilyKey;
  subjectKind: RecordSubjectKind;
  subjectId: string;
  clientLabel: string | null;
  locale: AdviceLocale;
  source: RecordPrefillSource | null;
  quantPortfolioId: string | null;
  documents: readonly File[] | null;
  fromNewClient?: boolean;
  meetingFormat?: MeetingFormat | null;
  resume?: AdvicePrepareResume | null;
  scenario?: PrepareScenario | null;
  autoStart?: boolean;
}

export interface PrepareScenario {
  deposit: number | null;
  withdrawal: number | null;
  buffer: number | null;
  reason: string | null;
  timeHorizonYears: number | null;
  timeHorizonComment: string | null;
  sustainability: string | null;
}

export interface AdvicePrepareResume {
  prospectSetId: string;
  prospectPortfolioId: number;
  transactionSetId: string | null;
  fromRecordId: string;
}

export type MeetingFormat = (typeof MEETING_FORMATS)[number];

export type RecordPrefillSource = {
  family: RecordFamilyKey;
  id: string;
};

export const MEETING_FORMATS = ["PHYSICAL", "DIGITAL", "TELEPHONE"] as const;

let state: RecordPrepareState | null = null;
const listeners = new Set<() => void>();

export function useRecordPrepare(): RecordPrepareState | null {
  return useSyncExternalStore(subscribe, () => state);
}

export function peekRecordPrepare(): RecordPrepareState | null {
  return state;
}

export function openRecordPrepare(next: Omit<RecordPrepareState, "id">) {
  setState({ ...next, id: crypto.randomUUID() });
}

export function preparedMeetingFormat(
  family: RecordFamilyKey,
  subjectId: string,
): MeetingFormat | null {
  if (state === null || state.family !== family || state.subjectId !== subjectId) return null;
  return state.meetingFormat ?? null;
}

export function clearRecordPrepare() {
  if (state !== null) setState(null);
}

function setState(next: RecordPrepareState | null) {
  state = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
